import React, { useContext } from "react";
import { PoseLandmarker } from "@mediapipe/tasks-vision";

// Import Contexts
import { ClinicContext, LanguageContext, ScreenContext } from "contexts";

// Import Components
import { TitleBar, Scan } from "components/index";
import { Language, ProcedureType, TScreen } from "api/type";
import { getTranslation } from "utils/translate";
import { isAISupportedProcedure } from "utils/isAISuportedProcedureAndArea";
import { mixpanelTrack } from "utils/mixpanel";
import { TImageBlobAndViewType } from "components/scan/Scan";

type ScreenScanProps = {
  selectedProcedure: ProcedureType | null;
  poseLandmarker: PoseLandmarker | "ERROR" | null;
  handleSaveBlobAndPresignedUrl: (
    blobAndPresignedUrl: TImageBlobAndViewType | null,
  ) => void;
};

// handleSaveBlobAndPresignedUrl = { handleSaveBlobAndPresignedUrl };
const ScreenScan: React.FC<ScreenScanProps> = ({
  selectedProcedure,
  poseLandmarker,
  handleSaveBlobAndPresignedUrl,
}) => {
  // Context
  const setScreen = useContext(ScreenContext)[1];
  const clinic = useContext(ClinicContext);
  const language = useContext<Language>(LanguageContext)[0];

  React.useEffect(() => {
    if (!clinic || !selectedProcedure) return;
    if (document.referrer.includes("staging")) return;
    mixpanelTrack("Photo Capture Started", [
      {
        propertyName: "Clinic Name",
        propertyValue: clinic.clinic_name,
      },
      {
        propertyName: "Procedure",
        propertyValue: selectedProcedure,
      },
    ]);
  }, [clinic, selectedProcedure]);

  const handleSaveImageBlobsAndViewTypes = (
    imageBlobsAndViewTypes: TImageBlobAndViewType[],
  ) => {
    if (imageBlobsAndViewTypes.length !== 0) {
      handleSaveBlobAndPresignedUrl(imageBlobsAndViewTypes[0]);
      if (selectedProcedure && isAISupportedProcedure(selectedProcedure)) {
        setScreen(TScreen.SIMULATION_VIEW_SCREEN);
      } else {
        setScreen(TScreen.SIMULATION_VIEW_SCREEN_NON_AI_SUPPORT);
      }

      if (!clinic || !selectedProcedure) return;
      if (document.referrer.includes("staging")) return;
      mixpanelTrack("Photo Capture Finished", [
        {
          propertyName: "Clinic Name",
          propertyValue: clinic.clinic_name,
        },
        {
          propertyName: "Procedure",
          propertyValue: selectedProcedure,
        },
      ]);
    }
  };

  return (
    <div className="screen-container">
      <TitleBar
        name={getTranslation("scan", language)}
        centerTitle
        showLogo={false}
        onClickBackButton={() =>
          setScreen(TScreen.BODY_SCAN_INSTRUCTIONS_SCREEN)
        }
      />
      <Scan
        poseLandmarker={poseLandmarker}
        selectedProcedure={selectedProcedure}
        handleSaveImageBlobsAndViewTypes={handleSaveImageBlobsAndViewTypes}
      />
    </div>
  );
};

export default ScreenScan;
